'use client';

import { useFormContext } from 'react-hook-form';

import type { LanguageMultiSelectorProps } from '@/components/Language/LanguageMultiSelector';
import LanguageMultiSelector from '@/components/Language/LanguageMultiSelector';
import type { GeneralFormProps } from '@/types/general';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '../ui/form';

type RHFLanguageMultiSelectorProps = {
  name: string;
  title?: string;
  description?: string;
  /**
   * Canonical helper text shown below the field.
   * Falls back to `description` for backward compatibility.
   */
  helperText?: string;
} & Omit<LanguageMultiSelectorProps, 'name' | 'title' | 'description' | 'value' | 'onChange'>
& Omit<GeneralFormProps, 'onChange'>;

/**
 * React Hook Form wrapper for LanguageMultiSelector component
 * Follows the established RHF pattern from other form components
 */
function RHFLanguageMultiSelector({
  name,
  title,
  description,
  helperText,
  required,
  ...languageSelectorProps
}: RHFLanguageMultiSelectorProps) {
  const { control } = useFormContext();
  const resolvedHelperText = helperText ?? description;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <FormItem className="w-full">
          <FormControl>
            <LanguageMultiSelector
              name={name}
              title={title}
              value={field.value || []}
              onChange={field.onChange}
              {...languageSelectorProps}
            />
          </FormControl>
          {fieldState.error === undefined && resolvedHelperText !== undefined && resolvedHelperText !== '' && (
            <FormDescription className="mt-1 type-body-s text-on-surface-variant">
              {resolvedHelperText}
            </FormDescription>
          )}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export default RHFLanguageMultiSelector;
